import { React, useContext, useEffect, useMemo, useState } from "react";
import { AppContext } from "../../context/AppContext";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Modal, Spinner } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import View from "../../assets/images/Group.svg";
import ApiConfig from "../../api/ApiConfig";
import {
  simplePostCall,
  getWithAuthCall,
} from "../../api/ApiServices";
import { notifySuccess, notifyError } from "../../sharedComponent/notify";
import { DateDDMMYYYY } from "../../sharedComponent/common";
import Loader from "../../sharedComponent/Loader";
import NoDataComp from "../../sharedComponent/NoDataComp";
import {
  useVehicles,
  VehicleSelect,
  vehicleNameById,
  ConfirmDialog,
} from "./ffHelpers";
import "../vehicleExpenses/vehicleAccident/VehicleExpenseHeader.scss";
import "./FleetFinance.scss";

const animations = {
  initial: { opacity: 0, x: 400 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 100 },
};

const emptyLine = { gl_account_id: "", debit: "", credit: "", narration: "" };

// Journal entries carry no explicit module field; the source module is encoded
// in the reference prefix (e.g. FUEL-77, SPARE-12, ACC-83). Map the known
// prefixes to friendly module names, falling back to the raw prefix.
const MODULE_BY_PREFIX = {
  FUEL: "Fuel Expenses",
  SPARE: "Spare Parts",
  ACC: "Accident",
  FINE: "Vehicle Fine",
  INV: "Invoice",
  DEP: "Depreciation",
  LEASE: "Lease",
  JE: "Manual Entry",
};

const moduleName = (reference) => {
  if (!reference) return "-";
  const prefix = String(reference).split(/[-_\s]/)[0].toUpperCase();
  return MODULE_BY_PREFIX[prefix] || prefix || "-";
};

const JournalEntries = () => {
  const { sidebar } = useContext(AppContext);
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const recordsPerPage = 10;

  const [accounts, setAccounts] = useState([]);
  const vehicles = useVehicles(); // U-01: shared, cached vehicle list
  const [vehicleFilter, setVehicleFilter] = useState("");
  // entry_id -> array of account names (list API has no account data, so we
  // pull each entry's lines from the single endpoint and merge them in).
  const [entryAccounts, setEntryAccounts] = useState({});

  // Add entry modal
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [header, setHeader] = useState({ description: "", reference: "", vehicle_id: "" });
  const [lines, setLines] = useState([{ ...emptyLine }, { ...emptyLine }]);

  // View modal
  const [showView, setShowView] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  // FF-10 / U-08: entry awaiting reverse confirmation
  const [reverseTarget, setReverseTarget] = useState(null);
  const [reversing, setReversing] = useState(false);

  useEffect(() => {
    getAccounts();
  }, []);

  // show vehicle name/number instead of the raw id (U-01)
  const vehicleLabel = (id) => vehicleNameById(vehicles, id);

  useEffect(() => {
    getEntries(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const getAccounts = () => {
    simplePostCall(
      ApiConfig.FLEET_FINANCE_GL_ACCOUNT_LIST,
      JSON.stringify({ account_type: null, is_active: true })
    )
      .then((data) => {
        if (data?.success || data?.result) setAccounts(data?.data || []);
      })
      .catch((err) => console.log("acc dropdown err", err));
  };

  const getEntries = (currentPage) => {
    setLoading(true);
    setEntryAccounts({});
    const body = JSON.stringify({
      page: currentPage,
      page_limit: recordsPerPage,
      vehicle_id: vehicleFilter ? Number(vehicleFilter) : undefined,
    });
    simplePostCall(ApiConfig.FLEET_FINANCE_JOURNAL_LIST, body)
      .then((data) => {
        if (data?.success || data?.result) {
          const rows = data?.data || [];
          setEntries(rows);
          setTotalCount(data?.total_count ?? 0);
          loadEntryAccounts(rows);
        } else {
          setEntries([]);
        }
      })
      .catch((err) => {
        console.log("journal list err", err);
        setEntries([]);
      })
      .finally(() => setLoading(false));
  };

  // type is "Dr" when the line has a debit, "Cr" when it has a credit.
  const mapLines = (raw) =>
    (raw || []).map((l) => {
      const debit = Number(l.debit) || 0;
      const credit = Number(l.credit) || 0;
      return {
        name: l.account_name || l.account_code || l.gl_account_id,
        type: debit > 0 ? "Dr" : "Cr",
        amount: debit > 0 ? debit : credit,
        glId: l.gl_account_id,
        code: l.account_code,
      };
    });

  // Map entry_id -> [{name, type, amount}]. TC_2013: rows that already carry
  // `lines` in the list response are used as-is; journal/single is only called
  // (in parallel) for rows without them. The amount column never needs it.
  const loadEntryAccounts = (rows) => {
    if (!rows.length) return;
    const map = {};
    const missing = [];
    rows.forEach((en) => {
      if (Array.isArray(en.lines)) map[en.entry_id] = mapLines(en.lines);
      else missing.push(en);
    });
    if (!missing.length) {
      setEntryAccounts(map);
      return;
    }
    Promise.all(
      missing.map((en) =>
        getWithAuthCall(ApiConfig.FLEET_FINANCE_JOURNAL_SINGLE + en.entry_id)
          .then((d) => [en.entry_id, mapLines(d?.data?.lines)])
          .catch(() => [en.entry_id, []])
      )
    ).then((pairs) => {
      pairs.forEach(([id, lines]) => {
        map[id] = lines;
      });
      setEntryAccounts({ ...map });
    });
  };

  // FF-10: a reversal entry must not offer Reverse (the server refuses it anyway)
  const isReversalEntry = (en) =>
    !!en.is_reversal ||
    !!en.reversal_of ||
    !!en.reversed_entry_id ||
    String(en.source_event || "").toLowerCase() === "reversal" ||
    /^reversal of/i.test(String(en.description || ""));

  // U-05: entry total shown as a positive number (total debit)
  const entryTotal = (en) => {
    const raw = en.total_debit ?? en.amount;
    if (raw !== null && raw !== undefined && raw !== "" && !Number.isNaN(Number(raw)))
      return Math.abs(Number(raw));
    const ls = entryAccounts[en.entry_id];
    if (ls && ls.length)
      return ls.reduce((s, l) => s + (l.type === "Dr" ? l.amount : 0), 0);
    return null;
  };

  const applyFilter = () => {
    if (page === 1) getEntries(1);
    else setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / recordsPerPage));
  // FF-17: "Showing a–b of n" computed from the current page
  const rangeStart = totalCount && entries.length ? (page - 1) * recordsPerPage + 1 : 0;
  const rangeEnd = Math.min(totalCount, (page - 1) * recordsPerPage + entries.length);

  // gl_account_id -> account_type ("revenue" | "expense" | "asset" | "liability").
  const accTypeById = useMemo(() => {
    const m = {};
    accounts.forEach((a) => {
      m[a.gl_account_id] = a.account_type;
    });
    return m;
  }, [accounts]);

  // Fallback classification from the account code prefix (4xxx revenue, 6/7xxx expense).
  const codeType = (code) => {
    const c = String(code || "");
    if (c.startsWith("4")) return "revenue";
    if (c.startsWith("6") || c.startsWith("7")) return "expense";
    return "";
  };

  // Decide whether an entry is income (+) or expense (-) from its P&L line.
  const entrySign = (entryId) => {
    const lines = entryAccounts[entryId];
    if (!lines || !lines.length) return null;
    const types = lines.map((l) => accTypeById[l.glId] || codeType(l.code));
    if (types.includes("revenue")) return "+";
    if (types.includes("expense")) return "−";
    return null; // pure balance-sheet movement, no income/expense impact
  };

  // ---- Add entry helpers ----
  const openAdd = () => {
    setHeader({ description: "", reference: "", vehicle_id: "" });
    setLines([{ ...emptyLine }, { ...emptyLine }]);
    setShowForm(true);
  };

  const changeHeader = (e) => {
    const { name, value } = e.target;
    setHeader((prev) => ({ ...prev, [name]: value }));
  };

  const changeLine = (idx, field, value) => {
    setLines((prev) =>
      prev.map((ln, i) => (i === idx ? { ...ln, [field]: value } : ln))
    );
  };

  const addLine = () => setLines((prev) => [...prev, { ...emptyLine }]);
  const removeLine = (idx) =>
    setLines((prev) => prev.filter((_, i) => i !== idx));

  const totalDebit = lines.reduce((s, l) => s + (Number(l.debit) || 0), 0);
  const totalCredit = lines.reduce((s, l) => s + (Number(l.credit) || 0), 0);
  // FF-16: besides equal totals, need >= 2 lines with an account and no line
  // that carries both a debit and a credit.
  const accountLines = lines.filter((l) => l.gl_account_id).length;
  const hasMixedLine = lines.some(
    (l) => (Number(l.debit) || 0) > 0 && (Number(l.credit) || 0) > 0
  );
  const totalsMatch = totalDebit === totalCredit && totalDebit > 0;
  const isBalanced = totalsMatch && accountLines >= 2 && !hasMixedLine;

  const handleSubmit = (e) => {
    e.preventDefault();
    // FF-16
    if (hasMixedLine) {
      notifyError("A line cannot have both a debit and a credit amount");
      return;
    }
    if (accountLines < 2) {
      notifyError("Select a GL account on at least two lines");
      return;
    }
    if (!totalsMatch) {
      notifyError("Entry is not balanced (total debit must equal total credit)");
      return;
    }
    const payloadLines = lines
      .filter((l) => l.gl_account_id && (Number(l.debit) || Number(l.credit)))
      .map((l) => {
        const line = { gl_account_id: Number(l.gl_account_id) };
        if (Number(l.debit)) line.debit = Number(l.debit);
        if (Number(l.credit)) line.credit = Number(l.credit);
        if (l.narration) line.narration = l.narration;
        return line;
      });
    const body = JSON.stringify({
      description: header.description,
      reference: header.reference || undefined,
      vehicle_id: header.vehicle_id ? Number(header.vehicle_id) : undefined,
      lines: payloadLines,
    });
    setSaving(true);
    simplePostCall(ApiConfig.FLEET_FINANCE_JOURNAL_ADD, body)
      .then((data) => {
        if (data?.success || data?.result) {
          notifySuccess(data?.message || "Journal posted");
          setShowForm(false);
          applyFilter();
        } else {
          notifyError(data?.message || "Unable to post journal");
        }
      })
      .catch((err) => {
        console.log("journal add err", err);
        notifyError("Something went wrong");
      })
      .finally(() => setSaving(false));
  };

  // ---- View / reverse ----
  const openView = (entryId) => {
    setShowView(true);
    setViewLoading(true);
    setViewData(null);
    getWithAuthCall(ApiConfig.FLEET_FINANCE_JOURNAL_SINGLE + entryId)
      .then((data) => {
        if (data?.success || data?.result) setViewData(data?.data || null);
        else notifyError(data?.message || "Unable to load entry");
      })
      .catch((err) => {
        console.log("journal single err", err);
        notifyError("Something went wrong");
      })
      .finally(() => setViewLoading(false));
  };

  const handleReverse = (entryId) =>
    simplePostCall(
      ApiConfig.FLEET_FINANCE_JOURNAL_REVERSE,
      JSON.stringify({ entry_id: entryId })
    )
      .then((data) => {
        if (data?.success || data?.result) {
          notifySuccess(data?.message || "Entry reversed");
          getEntries(page);
        } else {
          notifyError(data?.message || "Unable to reverse entry");
        }
      })
      .catch((err) => {
        console.log("reverse err", err);
        notifyError("Something went wrong");
      });

  // FF-10 / U-08: only reverse after the user confirms
  const confirmReverse = () => {
    if (!reverseTarget) return;
    setReversing(true);
    handleReverse(reverseTarget.entry_id).finally(() => {
      setReversing(false);
      setReverseTarget(null);
    });
  };

  return (
    <motion.div
      className={sidebar ? "taskMain " : "cx-active taskMain"}
      id="cx-main"
      variants={animations}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.1 }}
    >
      <div id="cx-wrapper" className="Fleet_Finance_Journal">
        <div
          className="displayFlexInp- displayFlexInp-for-expense-header"
          style={{ justifyContent: "space-between", alignItems: "center" }}
        >
          <div
            className="d-flex align-items-center flex-wrap"
            style={{ gap: 12 }}
          >
            <div
              className="form_input_main"
              style={{ width: 240, marginBottom: 0 }}
            >
              {/* U-01 */}
              <VehicleSelect
                vehicles={vehicles}
                value={vehicleFilter}
                name="vehicle_id"
                allOption={t("All vehicles")}
                onChange={(e) => setVehicleFilter(e.target.value)}
              />
            </div>
            <button className="cx-btn-1" onClick={applyFilter}>
              {t("Search")}
            </button>
          </div>
          <button className="cx-btn-2" onClick={openAdd}>
            + {t("Post Journal")}
          </button>
        </div>

        {loading ? (
          <Loader />
        ) : (
          <div className="main-master-wrapper">
            <div id="scroll_insideThe_Padding_tabel">
              <div className="VA_table">
                <table className="table tableAdmin">
                  <thead className="tableHead">
                    <tr>
                      <th>{t("Entry ID")}</th>
                      <th>{t("Date")}</th>
                      <th>{t("Description")}</th>
                      <th>{t("Accounts")}</th>
                      <th>{t("Reference")}</th>
                      <th>{t("Module")}</th>
                      <th>{t("Vehicle")}</th>
                      <th>{t("Amount")}</th>
                      <th>{t("Status")}</th>
                      <th>{t("Action")}</th>
                    </tr>
                  </thead>
                  <tbody className="tableBody">
                    {entries && entries.length > 0 ? (
                      entries.map((en) => (
                        <tr key={en.entry_id}>
                          <td>{en.entry_id}</td>
                          <td>
                            {en.entry_date || en.created_at
                              ? DateDDMMYYYY(en.entry_date || en.created_at)
                              : "-"}
                          </td>
                          <td>{en.description}</td>
                          <td>
                            {entryAccounts[en.entry_id] ? (
                              entryAccounts[en.entry_id].length ? (
                                <div className="ff-lines">
                                  {entryAccounts[en.entry_id].map((ln, i) => (
                                    <div
                                      key={i}
                                      className={`ff-line ${
                                        ln.type === "Dr" ? "ff-dr" : "ff-cr"
                                      }`}
                                    >
                                      <span
                                        className="ff-dc-tag"
                                        title={
                                          ln.type === "Dr" ? "Debit" : "Credit"
                                        }
                                      >
                                        {ln.type}
                                      </span>
                                      <span className="ff-dc-name">
                                        {ln.name}
                                      </span>
                                      <span className="ff-dc-amount">
                                        {ln.type === "Dr" ? "+" : "−"}
                                        {Number(ln.amount).toLocaleString(
                                          undefined,
                                          { minimumFractionDigits: 2 }
                                        )}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                "-"
                              )
                            ) : (
                              "…"
                            )}
                          </td>
                          <td>{en.reference || "-"}</td>
                          <td>
                            <span className="ff-badge ff-badge-info">
                              {moduleName(en.reference)}
                            </span>
                          </td>
                          <td>{vehicleLabel(en.vehicle_id)}</td>
                          <td>
                            {/* U-05: positive total; sign only drives the colour */}
                            {entryTotal(en) !== null ? (
                              (() => {
                                const sign = entrySign(en.entry_id);
                                return (
                                  <span
                                    className={`ff-amount ${
                                      sign === "+"
                                        ? "ff-amount-pos"
                                        : sign === "−"
                                        ? "ff-amount-neg"
                                        : ""
                                    }`}
                                    title={
                                      sign === "+"
                                        ? "Income"
                                        : sign === "−"
                                        ? "Expense"
                                        : undefined
                                    }
                                  >
                                    {entryTotal(en).toLocaleString(undefined, {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}
                                  </span>
                                );
                              })()
                            ) : (
                              "-"
                            )}
                          </td>
                          <td>{en.status || "posted"}</td>
                          <td>
                            <div className="d-flex align-items-center">
                              <img
                                src={View}
                                alt="view"
                                style={{ cursor: "pointer", marginRight: 12 }}
                                onClick={() => openView(en.entry_id)}
                              />
                              {en.status !== "reversed" && !isReversalEntry(en) && (
                                <button
                                  className="cx-btn-1"
                                  style={{ padding: "2px 10px" }}
                                  onClick={() => setReverseTarget(en)} // FF-10
                                >
                                  {t("Reverse")}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={10}>
                          <NoDataComp />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {totalCount > 0 && (
              <div className="ff-pagination">
                {/* FF-17: range follows the current page */}
                <p className="reg-color">
                  {t("Showing")} {rangeStart} - {rangeEnd} {t("of")} {totalCount}
                </p>
                <div className="ff-page-controls">
                  <button
                    type="button"
                    className="ff-page-btn"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    &#8249; {t("Previous")}
                  </button>
                  <span className="ff-page-current">
                    {t("Page")} {page} {t("of")} {totalPages}
                  </span>
                  <button
                    type="button"
                    className="ff-page-btn"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    {t("Next")} &#8250;
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add journal modal */}
      <Modal
        show={showForm}
        onHide={() => setShowForm(false)}
        centered
        size="lg"
        className="common-model ff-modal"
      >
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{t("Post Journal Entry")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row">
              <div className="col-md-6 form_input_main">
                <Form.Label className="common-labels">
                  {t("Description")} <span>&#42;</span>
                </Form.Label>
                <Form.Control
                  required
                  type="text"
                  name="description"
                  value={header.description}
                  onChange={changeHeader}
                  placeholder="Fuel purchase"
                />
              </div>
              <div className="col-md-3 form_input_main">
                <Form.Label className="common-labels">{t("Reference")}</Form.Label>
                <Form.Control
                  type="text"
                  name="reference"
                  value={header.reference}
                  onChange={changeHeader}
                  placeholder="INV-1001"
                />
              </div>
              <div className="col-md-3 form_input_main">
                <Form.Label className="common-labels">{t("Vehicle")}</Form.Label>
                {/* U-01 */}
                <VehicleSelect
                  vehicles={vehicles}
                  value={header.vehicle_id}
                  name="vehicle_id"
                  placeholder={t("Select Vehicle")}
                  onChange={changeHeader}
                />
              </div>
            </div>

            <div className="VA_table" style={{ marginTop: 8 }}>
              <table className="table tableAdmin">
                <thead className="tableHead">
                  <tr>
                    <th>{t("GL Account")}</th>
                    <th>{t("Debit")}</th>
                    <th>{t("Credit")}</th>
                    <th>{t("Narration")}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody className="tableBody">
                  {lines.map((ln, idx) => (
                    <tr key={idx}>
                      <td>
                        <Form.Select
                          value={ln.gl_account_id}
                          onChange={(e) =>
                            changeLine(idx, "gl_account_id", e.target.value)
                          }
                        >
                          <option value="">{t("Select")}</option>
                          {accounts.map((a) => (
                            <option key={a.gl_account_id} value={a.gl_account_id}>
                              {a.account_code} - {a.account_name}
                            </option>
                          ))}
                        </Form.Select>
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          value={ln.debit}
                          onChange={(e) =>
                            changeLine(idx, "debit", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          value={ln.credit}
                          onChange={(e) =>
                            changeLine(idx, "credit", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          value={ln.narration}
                          onChange={(e) =>
                            changeLine(idx, "narration", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        {lines.length > 2 && (
                          <button
                            type="button"
                            className="cx-btn-1"
                            style={{ padding: "2px 10px" }}
                            onClick={() => removeLine(idx)}
                          >
                            &#10005;
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="d-flex justify-content-between align-items-center" style={{ marginTop: 8 }}>
              <button type="button" className="cx-btn-1" onClick={addLine}>
                + {t("Add Line")}
              </button>
              <div>
                <span style={{ marginRight: 16 }}>
                  {t("Total Debit")}: <b>{totalDebit}</b>
                </span>
                <span style={{ marginRight: 16 }}>
                  {t("Total Credit")}: <b>{totalCredit}</b>
                </span>
                <span style={{ color: isBalanced ? "green" : "red" }}>
                  {isBalanced ? t("Balanced") : t("Not balanced")}
                </span>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className="pop-up-modal-footer">
            <div className="btn-wrapper">
              <button
                type="button"
                className="cx-btn-1"
                onClick={() => setShowForm(false)}
              >
                {t("Cancel")}
              </button>
              <button
                type="submit"
                className="cx-btn-2"
                disabled={saving || !isBalanced}
              >
                {saving ? <Spinner animation="border" size="sm" /> : t("Post")}
              </button>
            </div>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* View modal */}
      <Modal
        show={showView}
        onHide={() => setShowView(false)}
        centered
        size="lg"
        className="common-model ff-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("Journal Entry Details")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {viewLoading ? (
            <Loader />
          ) : viewData ? (
            <>
              <div className="row">
                <div className="col-md-4">
                  <b>{t("Entry ID")}:</b> {viewData.entry_id}
                </div>
                <div className="col-md-4">
                  <b>{t("Reference")}:</b> {viewData.reference || "-"}
                </div>
                <div className="col-md-4">
                  <b>{t("Status")}:</b> {viewData.status || "posted"}
                </div>
                <div className="col-md-12" style={{ marginTop: 8 }}>
                  <b>{t("Description")}:</b> {viewData.description}
                </div>
              </div>
              <div className="VA_table" style={{ marginTop: 12 }}>
                <table className="table tableAdmin">
                  <thead className="tableHead">
                    <tr>
                      <th>{t("Account")}</th>
                      <th>{t("Debit")}</th>
                      <th>{t("Credit")}</th>
                      <th>{t("Narration")}</th>
                    </tr>
                  </thead>
                  <tbody className="tableBody">
                    {(viewData.lines || []).map((l, i) => (
                      <tr key={i}>
                        <td>
                          {l.account_code
                            ? `${l.account_code} - ${l.account_name || ""}`
                            : l.gl_account_id}
                        </td>
                        <td>{l.debit || 0}</td>
                        <td>{l.credit || 0}</td>
                        <td>{l.narration || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <NoDataComp />
          )}
        </Modal.Body>
      </Modal>

      {/* FF-10 / U-08: reverse confirmation */}
      <ConfirmDialog
        show={!!reverseTarget}
        title={t("Reverse journal entry")}
        body={`This posts a reversing entry for ${
          reverseTarget?.reference || reverseTarget?.entry_no || `#${reverseTarget?.entry_id}`
        }. Continue?`}
        confirmText={t("Reverse")}
        busy={reversing}
        onConfirm={confirmReverse}
        onCancel={() => !reversing && setReverseTarget(null)}
      />
    </motion.div>
  );
};

export default JournalEntries;
